import { UnderlyingCard } from 'components/shared/underlyings/underlyings-card/underlying-card';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { isEqual } from 'lodash-es';
import {
  SingleUnderlyingSearchEditableFieldMetadata,
  UnderlyingsSearchEditableFieldMetadata,
} from 'models/forms/fields';
import { UnderlyingsSearchFieldMetadata } from 'models/forms/fields/underlyings-search';
import { Underlying } from 'models/underlying';
import { ErrorWrapper } from 'routes/essential-workspace/components/form/error-wrapper';
import { useFormErrors } from 'routes/essential-workspace/components/form/use-form-errors';
import { useFormFieldData } from 'routes/essential-workspace/components/form/use-form-field-data';
import { isWorkspaceEssentialTabUnderlyingsResultExpanded } from 'state/selectors';
import { toggleEssentialWorkspaceUnderlyingResultThunk } from 'state/workspaces/essential/thunks';

type Props = Pick<
  UnderlyingsSearchEditableFieldMetadata | UnderlyingsSearchFieldMetadata | SingleUnderlyingSearchEditableFieldMetadata,
  'id' | 'underlyingBlock'
>;

export function SingleUnderlying({ id, underlyingBlock }: Props) {
  const dispatch = useThunkDispatch();

  const tabId = useAssertedActiveTabId();
  const [underlyings, setUnderlyings] = useFormFieldData<ReadonlyArray<Underlying>>(id);

  const { getFieldError } = useFormErrors();
  const fieldError = getFieldError(id);

  const isUnderlyingResultExpanded = useAppSelector(s => isWorkspaceEssentialTabUnderlyingsResultExpanded(s, tabId));
  const toggleUnderlyingResult = () => dispatch(toggleEssentialWorkspaceUnderlyingResultThunk(tabId));

  const handleSetUnderlyings = (newUnderlying?: Underlying) => {
    const newUnderlyings = newUnderlying !== undefined ? [newUnderlying] : [];
    if (isEqual(underlyings, newUnderlyings)) {
      return;
    }
    setUnderlyings(newUnderlyings);
  };

  return (
    <ErrorWrapper error={fieldError}>
      <UnderlyingCard
        formId={tabId}
        underlyingBlock={underlyingBlock}
        underlying={underlyings.length > 0 ? underlyings[0] : undefined}
        setUnderlying={handleSetUnderlyings}
        isInvalid={fieldError?.severity === 'Error'}
        isHighlighted={fieldError?.severity === 'Warning'}
        isUnderlyingResultExpanded={isUnderlyingResultExpanded}
        onToggleUnderlyingResult={toggleUnderlyingResult}
      />
    </ErrorWrapper>
  );
}
